// src/pages/cron/CronPage.jsx
import { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Paper,
  Stack,
  Typography,
  TextField,
  Button,
  MenuItem,
  FormControlLabel,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Chip,
  Tooltip
} from '@mui/material';
import { deleteData, getData, postData, putData } from '../../api/services'; // <- adjust to your path
import { FaPlus, FaRegEdit, FaRegEye, FaRegTrashAlt } from 'react-icons/fa';
// import api from "../../utils/api";                       // <- axios instance with interceptors
// --- Simple cron validator (5 or 6 fields) ---
const CRON_REGEX = /^(\S+)\s+(\S+)\s+(\S+)\s+(\S+)\s+(\S+)(\s+\S+)?$/;
const isValidCron = (s) => typeof s === 'string' && CRON_REGEX.test(s.trim()) && !/[^\d*/,\-?L#A-Za-z]/.test(s.replace(/\s+/g, ''));

// --- Presets ---
const CRON_PRESETS = [
  { label: 'Every minute', value: '* * * * *' },
  { label: 'Every 15 minutes', value: '*/15 * * * *' },
  { label: 'Hourly', value: '0 * * * *' },
  { label: 'Daily at 02:00', value: '0 2 * * *' },
  { label: 'Weekly (Sun 03:00)', value: '0 3 * * 0' },
  { label: 'Monthly (1st 04:00)', value: '0 4 1 * *' }
];

// enum type options from model
const TYPE_OPTIONS = ['daily', 'weekly', 'monthly', 'custom'];

export default function CronPage() {
  // ---------- table state ----------
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // ---------- modal / form state ----------
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('create'); // 'create' | 'update' | 'view'
  const [editingId, setEditingId] = useState(null);

  const [form, setForm] = useState({
    name: '',
    cronTime: '',
    email: '', // comma-separated in UI → array on submit
    type: 'custom',
    isActive: true,
    description: ''
  });
  const [touched, setTouched] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // ---------- validation ----------
  const errors = useMemo(() => {
    const e = {};
    if (!form.name?.trim()) e.name = 'Name is required';
    if (!form.cronTime?.trim()) e.cronTime = 'Cron is required';
    else if (!isValidCron(form.cronTime)) e.cronTime = 'Invalid cron format (5 or 6 fields)';
    // email is required array of strings → require at least one
    if (!form.email?.trim()) e.email = 'At least one email is required';
    return e;
  }, [form]);

  const hasError = (k) => touched[k] && !!errors[k];

  // ---------- utils ----------
  const resetForm = () => {
    setForm({
      name: '',
      cronTime: '',
      email: '',
      type: 'custom',
      isActive: true,
      description: ''
    });
    setTouched({});
    setEditingId(null);
  };

  const openCreate = () => {
    setMode('create');
    resetForm();
    setOpen(true);
  };

  const openView = async (id) => {
    try {
      setMode('view');
      setEditingId(id);
      const data = await getData(`/get-cron-by-id/${id}`);
      const emails = Array.isArray(data?.email) ? data.email.join(', ') : '';
      setForm({
        name: data?.name ?? '',
        cronTime: data?.cronTime ?? '',
        email: emails,
        type: data?.type ?? 'custom',
        isActive: !!data?.isActive,
        description: data?.description ?? ''
      });
      setOpen(true);
    } catch (e) {
      console.error(e);
    }
  };

  const openUpdate = async (id) => {
    try {
      setMode('update');
      setEditingId(id);
      const data = await getData(`/get-cron-by-id/${id}`);
      const emails = Array.isArray(data?.email) ? data.email.join(', ') : '';
      setForm({
        name: data?.name ?? '',
        cronTime: data?.cronTime ?? '',
        email: emails,
        type: data?.type ?? 'custom',
        isActive: !!data?.isActive,
        description: data?.description ?? ''
      });
      setOpen(true);
    } catch (e) {
      console.error(e);
    }
  };

  const closeModal = () => {
    setOpen(false);
  };

  const handleChange = (key) => (evt) => {
    const value = key === 'isActive' ? evt.target.checked : evt.target.value;
    setForm((s) => ({ ...s, [key]: value }));
  };

  const handleBlur = (key) => () => setTouched((t) => ({ ...t, [key]: true }));

  const handlePreset = (value) => setForm((s) => ({ ...s, cronTime: value }));

  const parseEmailArray = (s) =>
    s
      .split(',')
      .map((x) => x.trim().toLowerCase())
      .filter(Boolean);

  // ---------- CRUD ----------
  const fetchAll = async () => {
    try {
      setLoading(true);
      const data = await getData('/get-all-cron');
      setRows(Array.isArray(data) ? data : (data?.items ?? []));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setTouched({ name: true, cronTime: true, email: true });

    if (Object.keys(errors).length > 0) {
      console.log('Please fix validation errors', 'warning');
      return;
    }

    const payload = {
      name: form.name.trim(),
      cronTime: form.cronTime.trim(),
      email: parseEmailArray(form.email),
      type: form.type,
      isActive: !!form.isActive,
      description: form.description?.trim() || null
    };

    try {
      setSubmitting(true);

      if (mode === 'create') {
        await postData('/create-cron', payload);
        console.log('Cron created', 'success');
      } else if (mode === 'update' && editingId) {
        await putData(`/update-cron/${editingId}`, payload);
        console.log('Cron updated', 'success');
      }

      closeModal();
      await fetchAll();
    } catch (err) {
      console.error('Save error:', err);
      // errors are toasted in interceptor
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this cron configuration?')) return;
    try {
      await deleteData(`/delete-cron/${id}`);
      console.log('Cron deleted', 'success');
      await fetchAll();
    } catch (e) {
      console.error(e);
    }
  };

  // ---------- render ----------
  return (
    <Box>
      {/* Header row with Create button */}
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5">Cron Configurations</Typography>
        <Button variant="contained" startIcon={<FaPlus />} onClick={openCreate}>
          Create
        </Button>
      </Stack>

      {/* Listing */}
      <Paper sx={{ p: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Cron</TableCell>
              <TableCell>Emails</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Active</TableCell>
              <TableCell>Description</TableCell>
              <TableCell width={170} align="right">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {!loading && rows?.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No cron configurations found.
                </TableCell>
              </TableRow>
            )}
            {rows?.map((r) => (
              <TableRow key={r._id}>
                <TableCell>{r.name}</TableCell>
                <TableCell>
                  <code>{r.cronTime}</code>
                </TableCell>
                <TableCell>
                  {Array.isArray(r.email) && r.email.length > 0 ? (
                    <Stack direction="row" spacing={1} flexWrap="wrap">
                      {r.email.slice(0, 3).map((e) => (
                        <Chip key={e} label={e} size="small" sx={{ mr: 0.5, mb: 0.5 }} />
                      ))}
                      {r.email.length > 3 && <Chip size="small" label={`+${r.email.length - 3}`} />}
                    </Stack>
                  ) : (
                    '-'
                  )}
                </TableCell>
                <TableCell>{r.type}</TableCell>
                <TableCell>{r.isActive ? 'Yes' : 'No'}</TableCell>
                <TableCell sx={{ maxWidth: 320, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {r.description ?? '-'}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="View">
                    <IconButton onClick={() => openView(r._id)} size="small" >
                      <FaRegEye style={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Update">
                    <IconButton onClick={() => openUpdate(r._id)} size="small">
                      <FaRegEdit style={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton onClick={() => handleDelete(r._id)} size="small">
                      <FaRegTrashAlt style={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
            {loading && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Loading…
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* Create/Update/View Modal */}
      <Dialog open={open} onClose={closeModal} fullWidth maxWidth="md">
        <DialogTitle>
          {mode === 'create' && 'Create Cron Configuration'}
          {mode === 'update' && 'Update Cron Configuration'}
          {mode === 'view' && 'View Cron Configuration'}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <TextField
              label="Name"
              value={form.name}
              onChange={handleChange('name')}
              onBlur={handleBlur('name')}
              error={hasError('name')}
              helperText={hasError('name') ? errors.name : 'Unique identifier'}
              fullWidth
              required
              disabled={mode === 'view'}
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Cron expression"
                value={form.cronTime}
                onChange={handleChange('cronTime')}
                onBlur={handleBlur('cronTime')}
                error={hasError('cronTime')}
                helperText={hasError('cronTime') ? errors.cronTime : 'e.g. "0 2 * * *" (minute hour day month weekday)'}
                fullWidth
                required
                disabled={mode === 'view'}
              />

              <TextField
                select
                label="Preset"
                value=""
                onChange={(e) => handlePreset(e.target.value)}
                sx={{ minWidth: { sm: 220 } }}
                disabled={mode === 'view'}
              >
                {CRON_PRESETS.map((p) => (
                  <MenuItem key={p.value} value={p.value}>
                    {p.label}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>

            <TextField
              label="Email recipients"
              value={form.email}
              onChange={handleChange('email')}
              onBlur={handleBlur('email')}
              error={hasError('email')}
              helperText={hasError('email') ? errors.email : 'Comma-separated emails (e.g. ops@acme.com, team@acme.com)'}
              placeholder="user1@example.com, user2@example.com"
              fullWidth
              required
              disabled={mode === 'view'}
            />

            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField select label="Type" value={form.type} onChange={handleChange('type')} fullWidth disabled={mode === 'view'}>
                {TYPE_OPTIONS.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </TextField>

              <FormControlLabel
                control={<Switch checked={form.isActive} onChange={handleChange('isActive')} color="primary" disabled={mode === 'view'} />}
                label={form.isActive ? 'Active' : 'Inactive'}
                sx={{ ml: { sm: 2 } }}
              />
            </Stack>

            <TextField
              label="Description"
              value={form.description}
              onChange={handleChange('description')}
              multiline
              minRows={3}
              placeholder="Optional notes about what this cron does"
              fullWidth
              disabled={mode === 'view'}
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={closeModal} color="inherit">
            {mode === 'view' ? 'Close' : 'Cancel'}
          </Button>
          {mode !== 'view' && (
            <Button onClick={handleSubmit} variant="contained" disabled={submitting}>
              {submitting ? 'Saving...' : mode === 'create' ? 'Create' : 'Update'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}
